import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Filter, Wrench, AlertTriangle, CheckCircle, Info, Settings, Gauge, ChevronRight, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

// Tool category icons and colors
const categoryStyles = {
  'End Mill': { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: '🔧' },
  'Upcut': { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: '⬆️' },
  'Downcut': { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: '⬇️' },
  'Compression Bit': { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: '↕️' },
  'V-Bit': { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: '✒️' },
  'Ball Nose': { color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', icon: '🔵' },
  'Surfacing / Flattening': { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: '🔲' },
  'O-Flute': { color: 'bg-pink-500/20 text-pink-400 border-pink-500/30', icon: '🌀' },
  'Composite cutters': { color: 'bg-gray-500/20 text-zinc-400 border-gray-500/30', icon: '💎' },
};

const levelColors = {
  'Hobby': 'bg-green-500/20 text-green-400',
  'Prosumer': 'bg-blue-500/20 text-blue-400',
  'Professional': 'bg-purple-500/20 text-purple-400',
  'Industrial': 'bg-red-500/20 text-red-400',
};

const BitSelector = () => {
  const { token } = useContext(AuthContext);
  const [tools, setTools] = useState([]);
  const [filteredTools, setFilteredTools] = useState([]);
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnlyCompatible, setShowOnlyCompatible] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [materialFilter, setMaterialFilter] = useState('all');
  
  // Unique values for filters
  const [categories, setCategories] = useState([]);
  const [levels, setLevels] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);

  useEffect(() => {
    fetchTools();
    fetchMachines();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tools, searchQuery, categoryFilter, levelFilter, materialFilter, showOnlyCompatible, selectedMachine]);

  const fetchTools = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tools-v2`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTools(response.data.tools || []);
      
      // Extract unique values for filters
      const cats = [...new Set((response.data.tools || []).map(t => t.category))];
      const lvls = [...new Set((response.data.tools || []).map(t => t.level))];
      const mats = [...new Set((response.data.tools || []).flatMap(t => t.materials))];
      
      setCategories(cats);
      setLevels(lvls);
      setAllMaterials(mats);
    } catch (error) {
      console.error('Failed to fetch tools:', error);
      setTools([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMachines = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/machines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMachines(response.data);
    } catch (error) {
      console.error('Failed to fetch machines:', error);
    }
  };

  // Helper function for collet compatibility check (used in applyFilters)
  const isColletCompatible = (tool) => {
    if (!selectedMachine) return true;
    const colletSize = selectedMachine.collet_size;
    if (!colletSize || !tool.diameters_mm?.length) return true;
    return tool.diameters_mm.some(d => d <= colletSize);
  };

  const applyFilters = () => {
    let result = [...tools];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.name.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query) ||
        t.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    if (categoryFilter !== 'all') {
      result = result.filter(t => t.category === categoryFilter);
    }
    
    if (levelFilter !== 'all') {
      result = result.filter(t => t.level === levelFilter);
    }
    
    if (materialFilter !== 'all') {
      result = result.filter(t => t.materials.includes(materialFilter));
    }
    
    // Filter by machine compatibility if enabled
    if (showOnlyCompatible && selectedMachine) {
      result = result.filter(t => isColletCompatible(t));
    }
    
    setFilteredTools(result);
  };

  const checkMachineCompatibility = (tool) => {
    if (!selectedMachine) return { compatible: true, warnings: [], colletCompatible: true };
    
    const warnings = [];
    const profile = selectedMachine.analysis_summary || selectedMachine;
    
    // Check collet size compatibility
    const colletSize = profile.collet_size || selectedMachine.collet_size;
    let colletCompatible = true;
    if (colletSize && tool.diameters_mm?.length > 0) {
      // Check if any tool diameter fits the collet
      const hasCompatibleDiameter = tool.diameters_mm.some(d => d <= colletSize);
      if (!hasCompatibleDiameter) {
        warnings.push(`Requires larger collet than your ${colletSize}mm collet can hold`);
        colletCompatible = false;
      }
    }
    
    // Check rigidity requirements
    const highRigidityTools = tool.tags?.includes('high_rigidity');
    const machineRigidity = profile.rigidity || 'unknown';
    
    if (highRigidityTools && machineRigidity === 'light') {
      warnings.push('This tool requires higher rigidity than your machine may provide');
    }
    
    // Check spindle compatibility
    const spindleType = profile.spindle_type_manual || 'unknown';
    if (tool.category === 'Surfacing / Flattening' && spindleType === 'trim_router') {
      warnings.push('Large surfacing bits may be too demanding for trim routers');
    }
    
    // Check diameter vs typical collet sizes
    const toolDiameters = tool.diameters_mm || [];
    const hasLargeDiameter = toolDiameters.some(d => d > 6.35);
    if (hasLargeDiameter && spindleType === 'trim_router') {
      warnings.push('Verify your collet can accommodate this tool diameter');
    }
    
    return {
      compatible: warnings.length === 0,
      warnings,
      colletCompatible
    };
  };

  const getRecommendedSettings = (tool) => {
    if (!selectedMachine) return null;
    
    const profile = selectedMachine.analysis_summary || selectedMachine;
    const rigidity = profile.rigidity || 'medium';
    
    // Adjust recommendations based on machine rigidity
    let rpmMultiplier = 1;
    let feedMultiplier = 1;
    let docMultiplier = 1;
    
    if (rigidity === 'light') {
      rpmMultiplier = 0.85;
      feedMultiplier = 0.7;
      docMultiplier = 0.5;
    } else if (rigidity === 'heavy') {
      rpmMultiplier = 1.1;
      feedMultiplier = 1.2;
      docMultiplier = 1.3;
    }
    
    const chiploadRange = tool.recommended_chipload;
    const rpmRange = tool.recommended_rpm_range;
    
    if (!chiploadRange || !rpmRange) return null;
    
    return {
      rpm: {
        min: Math.round(rpmRange.min * rpmMultiplier),
        max: Math.round(rpmRange.max * rpmMultiplier)
      },
      chipload: {
        min: (chiploadRange.min * feedMultiplier).toFixed(3),
        max: (chiploadRange.max * feedMultiplier).toFixed(3)
      },
      note: rigidity === 'light' ? 'Conservative settings for light machine' : 
            rigidity === 'heavy' ? 'Aggressive settings for rigid machine' : 'Standard settings'
    };
  };

  // Tool detail view
  if (selectedTool) {
    const compatibility = checkMachineCompatibility(selectedTool);
    const recommendedSettings = getRecommendedSettings(selectedTool);
    const style = categoryStyles[selectedTool.category] || { color: 'bg-gray-500/20 text-zinc-400 border-gray-500/30', icon: '🔧' };
    
    return (
      <div className="space-y-6">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedTool(null)}
          className="text-[#e67e22] hover:text-[#e67e22]/80 hover:bg-black/60"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tool List
        </Button>

        <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={style.color}>{selectedTool.category}</Badge>
                  <Badge className={levelColors[selectedTool.level]}>{selectedTool.level}</Badge>
                </div>
                <CardTitle className="text-2xl text-white">{selectedTool.name}</CardTitle>
              </div>
              <div className="text-4xl">{style.icon}</div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Machine Compatibility Warning */}
            {selectedMachine && !compatibility.compatible && (
              <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                  <div>
                    <div className="font-semibold text-amber-400">Compatibility Warnings for &quot;{selectedMachine.name}&quot;</div>
                    <ul className="text-amber-200/80 text-sm mt-1 space-y-1">
                      {compatibility.warnings.map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Descriptions */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4">
                <div className="text-xs text-[#e67e22] font-semibold uppercase mb-2">Usage</div>
                <p className="text-[#d6d6d6] text-sm">{selectedTool.descriptions?.usage}</p>
              </div>
              <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4">
                <div className="text-xs text-green-400 font-semibold uppercase mb-2">Benefits</div>
                <p className="text-[#d6d6d6] text-sm">{selectedTool.descriptions?.benefits}</p>
              </div>
              <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4">
                <div className="text-xs text-red-400 font-semibold uppercase mb-2">Limitations</div>
                <p className="text-[#d6d6d6] text-sm">{selectedTool.descriptions?.limitations}</p>
              </div>
            </div>

            {/* Specifications */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4">
                <div className="text-xs text-[#a3a3a3] font-semibold uppercase mb-3">Specifications</div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-[#a3a3a3]">Diameters:</span>
                    <span className="text-white">{selectedTool.diameters_mm?.map(d => `${d}mm`).join(', ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#a3a3a3]">Flute Count:</span>
                    <span className="text-white">{selectedTool.flute_count?.join(', ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#a3a3a3]">Operations:</span>
                    <span className="text-white text-right">{selectedTool.operation_types?.join(', ')}</span>
                  </div>
                </div>
              </div>

              <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4">
                <div className="text-xs text-[#a3a3a3] font-semibold uppercase mb-3">Recommended Settings</div>
                <div className="space-y-2">
                  {selectedTool.recommended_rpm_range && (
                    <div className="flex justify-between">
                      <span className="text-[#a3a3a3]">RPM Range:</span>
                      <span className="text-white">
                        {recommendedSettings?.rpm?.min || selectedTool.recommended_rpm_range.min} - {recommendedSettings?.rpm?.max || selectedTool.recommended_rpm_range.max}
                      </span>
                    </div>
                  )}
                  {selectedTool.recommended_chipload && (
                    <div className="flex justify-between">
                      <span className="text-[#a3a3a3]">Chipload (mm):</span>
                      <span className="text-white">
                        {recommendedSettings?.chipload?.min || selectedTool.recommended_chipload.min} - {recommendedSettings?.chipload?.max || selectedTool.recommended_chipload.max}
                      </span>
                    </div>
                  )}
                  {recommendedSettings?.note && (
                    <div className="text-xs text-[#e67e22] mt-2 italic">{recommendedSettings.note}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Compatible Materials */}
            <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4">
              <div className="text-xs text-[#a3a3a3] font-semibold uppercase mb-3">Compatible Materials</div>
              <div className="flex flex-wrap gap-2">
                {selectedTool.materials?.map((mat, i) => (
                  <Badge key={i} variant="outline" className="text-[#d6d6d6] border-[#3a3a3a]">
                    {mat}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Warnings */}
            {selectedTool.warnings?.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                  <div>
                    <div className="font-semibold text-red-400">Warnings</div>
                    <ul className="text-red-200/80 text-sm mt-1 space-y-1">
                      {selectedTool.warnings.map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Link to="/calculator" className="flex-1">
                <Button className="w-full bg-[#e67e22] hover:bg-[#e67e22]/90 text-black font-bold">
                  <Gauge className="w-4 h-4 mr-2" />
                  Calculate Feed & Speed
                </Button>
              </Link>
              <Link to="/tools/materials">
                <Button variant="outline" className="border-[#3a3a3a] text-[#d6d6d6]">
                  View Materials
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main tool list view
  return (
    <div 
      data-testid="bit-selector-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.FEEDS_SPEEDS)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="space-y-6">
          {/* Header */}
          <div>
            <button 
              onClick={() => window.history.back()} 
              className="text-[#e67e22] hover:text-[#e67e22]/80 text-sm flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">TOOL SELECTOR</div>
            <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-white mb-2">
              BIT SELECTOR
            </h1>
            <p className="text-lg text-[#d6d6d6]">
              Browse cutting tools by type, material compatibility, and machine requirements
            </p>
          </div>

          {/* Machine Selector */}
          {machines.length > 0 && (
            <Card className="bg-black/60 backdrop-blur-sm border-[#e67e22]/30">
              <CardContent className="pt-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-[#e67e22]" />
                    <span className="text-[#d6d6d6] font-medium">Machine Profile:</span>
                  </div>
                  <Select value={selectedMachine?.id || 'none'} onValueChange={(val) => {
                    const machine = machines.find(m => m.id === val);
                    setSelectedMachine(machine || null);
                    if (!machine) setShowOnlyCompatible(false);
                  }}>
                    <SelectTrigger className="w-64 bg-black/50 border-[#3a3a3a] text-white">
                      <SelectValue placeholder="Select your machine" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                      <SelectItem value="none" className="text-white">No machine selected</SelectItem>
                      {machines.map(m => (
                        <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedMachine && (
                    <>
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {selectedMachine.name}
                        {selectedMachine.collet_size && ` • ${selectedMachine.collet_size}mm collet`}
                      </Badge>
                      {selectedMachine.collet_size && (
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={showOnlyCompatible}
                            onChange={(e) => setShowOnlyCompatible(e.target.checked)}
                            className="w-4 h-4 rounded border-[#3a3a3a] bg-black/50 text-[#e67e22] focus:ring-[#e67e22]"
                          />
                          <span className="text-[#d6d6d6]">Show only compatible tools</span>
                        </label>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                    <Input
                      placeholder="Search tools..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3]"
                    />
                  </div>
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                    <SelectItem value="all" className="text-white">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Level" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                    <SelectItem value="all" className="text-white">All Levels</SelectItem>
                    {levels.map(lvl => (
                      <SelectItem key={lvl} value={lvl} className="text-white">{lvl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={materialFilter} onValueChange={setMaterialFilter}>
                  <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Material" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                    <SelectItem value="all" className="text-white">All Materials</SelectItem>
                    {allMaterials.map(mat => (
                      <SelectItem key={mat} value={mat} className="text-white">{mat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Results count */}
          <div className="text-[#a3a3a3] text-sm">
            Showing {filteredTools.length} of {tools.length} tools
          </div>

          {/* Tool Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTools.map(tool => {
              const style = categoryStyles[tool.category] || { color: 'bg-gray-500/20 text-zinc-400 border-gray-500/30', icon: '🔧' };
              const compatibility = checkMachineCompatibility(tool);
              
              return (
                <Card 
                  key={tool.id}
                  onClick={() => setSelectedTool(tool)}
                  className={`bg-black/60 backdrop-blur-sm border-[#3a3a3a] hover:border-[#e67e22]/50 transition-colors cursor-pointer group ${
                    selectedMachine && !compatibility.compatible ? 'border-amber-500/30' : ''
                  }`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={style.color}>{tool.category}</Badge>
                        <Badge className={levelColors[tool.level]}>{tool.level}</Badge>
                      </div>
                      <span className="text-2xl">{style.icon}</span>
                    </div>
                    <CardTitle className="text-white group-hover:text-[#e67e22] transition-colors text-lg mt-2">
                      {tool.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#a3a3a3] text-sm line-clamp-2 mb-3">
                      {tool.descriptions?.usage}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {tool.materials?.slice(0, 3).map((mat, i) => (
                        <Badge key={i} variant="outline" className="text-xs text-[#a3a3a3] border-[#3a3a3a]">
                          {mat}
                        </Badge>
                      ))}
                      {tool.materials?.length > 3 && (
                        <Badge variant="outline" className="text-xs text-[#a3a3a3] border-[#3a3a3a]">
                          +{tool.materials.length - 3} more
                        </Badge>
                      )}
                    </div>

                    {selectedMachine && !compatibility.compatible && (
                      <div className="flex items-center gap-1 text-amber-400 text-xs">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{!compatibility.colletCompatible ? 'Tool too large for collet' : 'May not be ideal for your machine'}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#3a3a3a]">
                      <div className="text-xs text-[#a3a3a3]">
                        {tool.diameters_mm?.map(d => `${d}mm`).join(', ')}
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#e67e22] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Empty State */}
          {filteredTools.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <Wrench className="w-16 h-16 text-[#a3a3a3] mx-auto mb-4" />
              <p className="text-[#d6d6d6] text-lg">No tools match your filters</p>
              <Button 
                variant="ghost" 
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('all');
                  setLevelFilter('all');
                  setMaterialFilter('all');
                  setShowOnlyCompatible(false);
                }}
                className="text-[#e67e22] mt-2"
              >
                Clear all filters
              </Button>
            </div>
          )}
      </div>
    </div>
  );
};

export default BitSelector;
